import { z } from 'zod';

/**
 * The pieces of acting for someone (APP-148) that the award, approval, budget and settings
 * contracts take as fields. They live apart from `acting-for.ts`, which needs those contracts'
 * own types, so the files never import each other in a circle. Import from `acting-for.js`.
 */

/** Who a Stock Admin may act for. */
export const ActForRole = z.enum(['executive', 'sector_lead']);
export type ActForRole = z.infer<typeof ActForRole>;

/** Sent with an action done for someone: who it is for, an optional note, and staged files. */
export const ActingFor = z
  .object({
    personId: z.uuid(),
    note: z.string().trim().max(2000).optional(),
    fileIds: z.array(z.uuid()).max(20).optional(),
  })
  .strict();
export type ActingFor = z.infer<typeof ActingFor>;

export const ActForPerson = z.object({
  id: z.uuid(),
  name: z.string(),
  title: z.string().nullable(),
});
export type ActForPerson = z.infer<typeof ActForPerson>;

/**
 * The fixed Sector Lead's line in every lead step (send, decide a delegate's entry, withdraw and
 * cancel; APP-148 R141): their role and sector, "Sector Lead · Maritime Airborne", never a
 * directory job title. One helper, so the steps can't drift apart.
 */
export const sectorLeadTitle = (sectorName: string): string => `Sector Lead · ${sectorName}`;

/** What a page offers a Stock Admin: who they may name, and whether it's filled in already. */
export const ActForOffer = z.object({
  role: ActForRole,
  people: z.array(ActForPerson),
  fixed: z.boolean(),
});
export type ActForOffer = z.infer<typeof ActForOffer>;

export const ActedForAction = z.enum([
  'approve',
  'decline',
  'reject',
  'return',
  'record_decision',
  'change_amounts',
  'change_split',
  'move_person',
  'enter_and_send',
  'send',
  'resend',
  'withdraw',
  'budget_change',
  'setting_change',
  'spot_funding_approve',
  'spot_funding_decline',
]);
export type ActedForAction = z.infer<typeof ActedForAction>;
